import { createFileRoute } from '@tanstack/react-router'
import { useSessionUser } from '@/hooks/use-session'
import { trpc } from '@/main'
import { skipToken, useQuery } from '@tanstack/react-query'
import { useClientOnlyQuery } from '@/hooks/use-client-only-query'
import { useEffect } from 'react'

export const Route = createFileRoute('/_landing/docs')({
  component: RouteComponent,
  pendingComponent: () => <div className="min-h-dvh" />,
  staticData: { hideFooter: true },
})

declare global {
  interface Window {
    Scalar?: {
      createApiReference: (
        elementId: string,
        options: { content: string; customCss: string; showDeveloperTools: 'never' },
      ) => void
    }
  }
}

const customCss = `
.light-mode {
  --scalar-color-1: var(--color-foreground);
  --scalar-color-2: var(--color-foreground);
  --scalar-color-3: var(--color-muted-foreground);
  --scalar-color-accent: var(--primary);
  --scalar-background-1: var(--color-background);
  --scalar-background-2: var(--color-card);
  --scalar-background-3: var(--color-muted);
  --scalar-background-accent: var(--color-accent);
  --scalar-border-color: var(--color-border);
  --scalar-sidebar-border-color: transparent;
}
.dark-mode {
  --scalar-color-1: var(--color-foreground);
  --scalar-color-2: var(--color-foreground);
  --scalar-color-3: var(--color-muted-foreground);
  --scalar-color-accent: var(--primary);
  --scalar-background-1: var(--color-background);
  --scalar-background-2: var(--color-card);
  --scalar-background-3: var(--color-muted);
  --scalar-background-accent: var(--color-accent);
  --scalar-border-color: var(--color-border);
  --scalar-sidebar-border-color: transparent;
}`

function RouteComponent() {
  // query: direct a relative-URL fetch with no origin on the server, so it can only run in the browser —
  // there is no trpc key here for a loader to own.
  const { data } = useClientOnlyQuery({
    queryKey: ['docs'],
    queryFn: async () => {
      const response = await fetch('/api/v1/openapi')
      if (!response.ok) {
        throw new Error('Failed to fetch API description')
      }
      return response.text()
    },
  })

  // The docs page is auth-optional: a logged-out visitor can read the API reference. The `apps`
  // queries are auth-required (a logged-out request 401s, which the global tRPC link in main.tsx
  // turns into a /login redirect), and they only exist to inject the logged-in user's own
  // clientSecret into the examples. So gate them on an authenticated user — anonymous visitors
  // just see the `YOUR_SECRET_TOKEN` placeholder.
  const user = useSessionUser()
  // query: direct skipToken-gated on an authenticated viewer; the page itself is auth-optional
  const { data: apps = [] } = useQuery(trpc.apps.getAll.queryOptions(user ? undefined : skipToken))
  const appId = apps[0]?.clientId ?? null
  // query: direct keyed off the app the query above just resolved, so no loader can own it
  const { data: app } = useQuery(trpc.apps.getOne.queryOptions(appId ?? skipToken))
  const content = data?.replaceAll('YOUR_SECRET_TOKEN', app?.app.clientSecret ?? 'YOUR_SECRET_TOKEN') ?? '{}'

  // We're injecting scalar instead of importing the react / node library because is too big of a dependency
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/@scalar/api-reference'
    script.addEventListener('load', () => {
      if (!window.Scalar) return
      window.Scalar.createApiReference('#scalar', { content, customCss, showDeveloperTools: 'never' })
    })
    document.body.appendChild(script)
    return () => script.remove()
  }, [content])

  return <div id="scalar" className="mx-auto min-h-dvh max-w-270" />
}
